/*
 * Join Tournament by Invite Code
 *
 * Two-stage flow:
 *   1) PREVIEW (anyone): fetch tournament preview from /tournaments/by-code/:code.
 *      Show name, location, team names. Big "Join" button.
 *   2) PICKER (auth required): on Join click, call /tournaments/by-code/:code/join.
 *      If the response includes unclaimedGuests, show "Are any of these you?"
 *      so the user can merge into their pre-seeded guest slot.
 *
 * If the user isn't logged in when they hit Join, we redirect to
 * /app/register?join=:code so Register can route back here after signup.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Trophy, Users, MapPin, Calendar, Check } from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { AppShell } from '@/components/AppShell';
import { ConfirmModal } from '@/components/ConfirmModal';
import { EventLogo } from '@/components/EventLogo';
import type { TournamentByCodePreview, EventPlayer } from '@golf-app/shared';

export default function JoinTournament() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const isLoggedIn = useAuthStore((s) => !!s.accessToken);
  const [searchParams] = useSearchParams();

  // Personal-claim params: `claim` is an event_player id and `token` is the
  // claim_token baked into the invitation email. Possession proves access to
  // the invitation, but the user still explicitly confirms before linking.
  const autoClaimSlotId = searchParams.get('claim');
  const autoClaimToken  = searchParams.get('token');
  const hasAutoClaim    = !!autoClaimSlotId && !!autoClaimToken;

  const [preview, setPreview] = useState<TournamentByCodePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [unclaimed, setUnclaimed] = useState<EventPlayer[] | null>(null);
  const [joinedEventId, setJoinedEventId] = useState<string | null>(null);
  // Tapping a name on the picker stages it here; the claim only fires
  // after the type-their-name confirmation. Claiming someone else's
  // slot takes over their handicap, team, and matches, so a fat-finger
  // tap shouldn't be enough (Travis's spec).
  const [pendingClaim, setPendingClaim] = useState<EventPlayer | null>(null);
  const [personalClaimOpen, setPersonalClaimOpen] = useState(false);
  const [personalClaimFailed, setPersonalClaimFailed] = useState(false);

  // Step 1: load the public preview.
  const loadPreview = useCallback(async () => {
    if (!code) return;
    setError(null);
    try {
      const p = await api.getTournamentByCode(code.toUpperCase());
      setPreview(p);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load this invite link.");
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // A signed-out user must authenticate first. Preserve the secure claim
  // params so this screen can show the explicit link confirmation afterward.
  useEffect(() => {
    if (!code || !hasAutoClaim || loading) return;
    if (!isLoggedIn) {
      const params = new URLSearchParams();
      params.set('join', code);
      params.set('claim', autoClaimSlotId!);
      params.set('token', autoClaimToken!);
      navigate(`/app/register?${params.toString()}`, { replace: true });
      return;
    }
    return;
  }, [code, hasAutoClaim, autoClaimSlotId, autoClaimToken, isLoggedIn, loading, navigate]);

  async function handlePersonalClaim() {
    if (!autoClaimSlotId || !autoClaimToken) return;
    setJoining(true);
    setError(null);
    try {
      const claimed = await api.claimEventPlayer(autoClaimSlotId, autoClaimToken);
      navigate(`/app/tournaments/${claimed.eventId}`, { replace: true });
    } catch (claimError: any) {
      setPersonalClaimFailed(true);
      setPersonalClaimOpen(false);
      setError(claimError?.message ?? "We couldn't link this invitation. You can still join below.");
    } finally {
      setJoining(false);
    }
  }

  // Step 2: when the user clicks Join.
  async function handleJoin() {
    if (!code) return;
    if (!isLoggedIn) {
      // Send them to register, then they come back here.
      navigate(`/app/register?join=${code}`, { replace: false });
      return;
    }
    setJoining(true);
    setError(null);
    try {
      const result = await api.joinTournamentByCode(code.toUpperCase());
      setJoinedEventId(result.id);
      if (result.unclaimedGuests && result.unclaimedGuests.length > 0) {
        setUnclaimed(result.unclaimedGuests);
      } else {
        // No guests to claim; just send them to the event.
        navigate(`/app/tournaments/${result.id}`, { replace: true });
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to join. Please try again.');
    } finally {
      setJoining(false);
    }
  }

  // Step 3: user picked a guest to claim (or "I'm new").
  async function claim(playerId?: string) {
    if (!code) return;
    setJoining(true);
    setError(null);
    try {
      const result = await api.joinTournamentByCode(code.toUpperCase(), playerId);
      navigate(`/app/tournaments/${result.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? "Couldn't claim that player. Try another option.");
      // Close the confirm so the error banner on the picker is visible.
      setPendingClaim(null);
    } finally {
      setJoining(false);
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }

  if (error && !preview) {
    return (
      <AppShell>
        <div className="t-empty">
          <div className="t-empty-icon"><Trophy /></div>
          <div className="t-empty-title">Invite link not valid</div>
          <p className="t-empty-hint">{error}</p>
          <Link to="/app/home" className="t-btn t-btn-secondary" style={{ marginTop: 16 }}>
            Go home
          </Link>
        </div>
      </AppShell>
    );
  }

  // PICKER stage: user joined and there are unclaimed guests.
  if (unclaimed && joinedEventId) {
    return (
      <AppShell>
        <section className="t-hero">
          <h1>Are any of these you?</h1>
          <p>
            Someone may have added you as a guest already. If you see your name, tap it.
            Otherwise, skip and we'll add you as a new player.
          </p>
        </section>

        {error && <div className="t-error">{error}</div>}

        {/* Two-column grid (Travis's spec): a 20-man roster as a single
            column is a half-mile scroll on a phone. Alphabetical so
            people can find their own name fast. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          {unclaimed
            .slice()
            .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' }))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className="t-row"
                disabled={joining}
                onClick={() => setPendingClaim(p)}
                style={{ textAlign: 'left', minWidth: 0 }}
              >
                <div className="t-row-body" style={{ minWidth: 0 }}>
                  <div className="t-row-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.displayName}
                  </div>
                  <div className="t-row-sub">
                    {p.eventHandicapIndex != null
                      ? `HCP ${p.eventHandicapIndex < 0 ? '+' + Math.abs(p.eventHandicapIndex) : p.eventHandicapIndex}`
                      : 'No handicap yet'}
                  </div>
                </div>
                <Check style={{ flexShrink: 0 }} />
              </button>
            ))}
        </div>

        <button
          type="button"
          className="t-btn t-btn-ghost t-btn-block"
          disabled={joining}
          onClick={() => claim()}
          style={{ marginTop: 16 }}
        >
          None of these. Add me as a new player.
        </button>

        {/* Type-their-name confirmation before claiming a roster slot.
            A tap alone shouldn't hand over someone's spot — typing the
            name is the solid confirmation Travis asked for. */}
        <ConfirmModal
          open={!!pendingClaim}
          title={`Please confirm you are ${pendingClaim?.displayName ?? ''}`}
          body={(
            <p>
              You're claiming <strong>{pendingClaim?.displayName}</strong>'s spot on the
              roster, including their handicap, team, and matches. If that's not you,
              go back and pick your own name.
            </p>
          )}
          requireType={pendingClaim?.displayName ?? ''}
          confirmLabel={`Yes, I'm ${pendingClaim?.displayName ?? ''}`}
          busy={joining}
          onCancel={() => { if (!joining) setPendingClaim(null); }}
          onConfirm={() => { if (pendingClaim) claim(pendingClaim.id); }}
        />
      </AppShell>
    );
  }

  // PREVIEW stage.
  if (!preview) return null;
  const { event, teams } = preview;
  return (
    <AppShell>
      <section className="t-hero" style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          {event.logoUrl
            ? <EventLogo logoUrl={event.logoUrl} name={event.name} size={80} />
            : <Trophy width={48} height={48} color="var(--green-bright)" />
          }
        </div>
        <p style={{ fontSize: 12, letterSpacing: 1.5, color: 'var(--text-sec)', fontWeight: 700, marginBottom: 4 }}>
          YOU'RE INVITED
        </p>
        <h1 style={{ marginBottom: 8 }}>{event.name}</h1>
        <p style={{ color: 'var(--text-sec)', display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
          {event.locationName && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MapPin width={14} height={14} /> {event.locationName}
            </span>
          )}
          {event.startDate && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar width={14} height={14} /> {formatDateRange(event.startDate, event.endDate)}
            </span>
          )}
        </p>
      </section>

      {teams.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 24,
            margin: '20px 0',
          }}
        >
          {teams.map((t) => (
            <div key={t.id} style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  background: t.primaryColor ?? 'var(--text-mut)',
                  display: 'inline-block',
                  marginBottom: 6,
                }}
              />
              <div style={{ fontWeight: 800, color: t.primaryColor ?? 'var(--text)' }}>
                {t.name}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="t-error">{error}</div>}

      {hasAutoClaim && isLoggedIn && !personalClaimFailed ? (
        <button
          type="button"
          className="t-btn t-btn-primary t-btn-block"
          disabled={joining}
          onClick={() => setPersonalClaimOpen(true)}
          style={{ marginTop: 16 }}
        >
          <Check /> Link my account to this invitation
        </button>
      ) : (
        <button
          type="button"
          className="t-btn t-btn-primary t-btn-block"
          disabled={joining}
          onClick={handleJoin}
          style={{ marginTop: 16 }}
        >
          <Users /> {joining ? 'Joining…' : isLoggedIn ? 'Join this Team Cup' : 'Sign up and join'}
        </button>
      )}

      <ConfirmModal
        open={personalClaimOpen}
        title="Link your account to this roster spot?"
        body={(
          <p>
            This keeps the name, team, handicap, captain role, and matches the
            organizer already assigned to this invitation.
          </p>
        )}
        confirmLabel="Confirm and link"
        busy={joining}
        onCancel={() => { if (!joining) setPersonalClaimOpen(false); }}
        onConfirm={handlePersonalClaim}
      />

      {isLoggedIn && (
        <p className="t-hint" style={{ textAlign: 'center', marginTop: 12 }}>
          Signed in. We'll add you to the event and let you claim your spot if you were already on the roster.
        </p>
      )}
      {!isLoggedIn && (
        <p className="t-hint" style={{ textAlign: 'center', marginTop: 12 }}>
          Need an account? Tap Join above and you'll be guided through a quick sign-up.
        </p>
      )}
    </AppShell>
  );
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return '';
  const s = new Date(start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (!end || end === start) return s;
  const e = new Date(end).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${s} – ${e}`;
}
